import React from 'react'
import {
    Autocomplete,
    Checkbox,
    FormControl,
    FormControlLabel,
    TextField,
} from '@mui/material'
import { BlockTypes, BlockFormState, ItemState } from '../../types'
import SelectList from '../../../../../Components/Elements/SelectList'
import DossierQuerySelect from '../../../../../Components/Elements/Select/DossierQuerySelect'
import {
    FormInputLabel,
    selectionControlLabelProps,
    selectionControlSx,
} from '@/Components/Elements/StyledComponents/Input'

interface GeneralTabProps {
    kedo: any
    state: BlockFormState
    handleChange: (
        event: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => void
    handleChangeItem: (key: keyof ItemState, val: any) => void
    isEntityRequired: boolean
}

type BlockTypeOption = { value: string; label: string }

const GeneralTab: React.FC<GeneralTabProps> = ({
    kedo,
    state,
    handleChange,
    handleChangeItem,
    isEntityRequired,
}) => {
    const blockTypeOptions: BlockTypeOption[] = Object.entries(BlockTypes).map(
        ([key, value]) => ({
            value,
            label: kedo.t(key),
        })
    )
    const selectedBlockType =
        blockTypeOptions.find(
            (option) => option.value === (state.item.type || '')
        ) || null
    const selectedDefDossier =
        state.defDossiers.find(
            (defDossier: any) =>
                String(defDossier.id) === String(state.item.defDossier || '')
        ) || null

    const changeItem = (name: string, value: string | number) => {
        handleChange({
            target: { name, value },
        } as React.ChangeEvent<HTMLInputElement>)
    }

    return (
        <>
            <FormControl fullWidth margin="normal">
                <FormInputLabel className="required">
                    {kedo.t('Block Type')}
                </FormInputLabel>
                <Autocomplete
                    size="small"
                    autoHighlight
                    autoSelect
                    options={blockTypeOptions}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                    }
                    value={selectedBlockType}
                    onChange={(_event, option) =>
                        changeItem('type', option ? option.value : '')
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder={kedo.t('Choose an option')}
                            error={Boolean(state.errors && state.errors.type)}
                            helperText={state.errors && state.errors.type}
                        />
                    )}
                />
            </FormControl>

            {isEntityRequired && (
                <FormControl fullWidth margin="normal">
                    <FormInputLabel className="required">
                        {kedo.t('Entity')}
                    </FormInputLabel>
                    <Autocomplete
                        size="small"
                        autoHighlight
                        autoSelect
                        options={state.defDossiers || []}
                        getOptionLabel={(option) =>
                            kedo.env().translateItem(option, 'defdossier') || ''
                        }
                        isOptionEqualToValue={(option, value) =>
                            String(option.id) === String(value.id)
                        }
                        value={selectedDefDossier}
                        onChange={(_event, option) => {
                            changeItem('defDossier', option ? option.id : '')
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder={kedo.t('Choose an option')}
                                error={Boolean(
                                    state.errors && state.errors.defDossier
                                )}
                                helperText={
                                    state.errors && state.errors.defDossier
                                }
                            />
                        )}
                    />
                </FormControl>
            )}

            <FormControlLabel
                {...selectionControlLabelProps}
                control={
                    <Checkbox
                        size="small"
                        name="dashboard"
                        checked={state.item.dashboard}
                        onChange={handleChange}
                        disabled={state.loading}
                        sx={(theme) => selectionControlSx(theme)}
                    />
                }
                label={kedo.t('Dashboard')}
                sx={{ ...selectionControlLabelProps.sx, mt: 1, mb: 1 }}
            />

            <FormControl fullWidth margin="normal">
                <FormInputLabel>{kedo.t('Parent block')}</FormInputLabel>
                <SelectList
                    currentValue={state.item.parent_id}
                    onClear={() => handleChangeItem('parent_id', null)}
                    searchName="search"
                    kedo={kedo}
                    urlParams={{
                        page: 0,
                        limit: 200,
                        type: 'block_container',
                        environment: kedo.env().getEnvironment().id,
                    }}
                    onDisplayName={(item: any) =>
                        kedo.env().translateItem(item, 'mypageblock')
                    }
                    url={kedo.api().getMyPageBlockEndpoint()}
                    onSelect={(item: any) =>
                        handleChangeItem('parent_id', item.id)
                    }
                />
            </FormControl>

            <DossierQuerySelect
                kedo={kedo}
                onChange={(newId: any) =>
                    handleChangeItem('dossierQuery', newId)
                }
                value={state.item.dossierQuery}
                ddId={state.item.defDossier}
            />
        </>
    )
}

export default GeneralTab
